/**
 *  @class
 *  @function FacetsToggle
 */
class FacetToggle extends HTMLElement {
	constructor() {
		super();
		this.init();
	}
	init() {
		this.drawer = document.getElementById('Facet-Drawer');
		this.container = this.closest('.facets--bar-sticky');

		// Add functionality to buttons
		this.addEventListener('click', (e) => {
			e.preventDefault();
			if (OverlayManager.isOpen(this.drawer)) {
				OverlayManager.close(this.drawer);
			} else {
				OverlayManager.open(this.drawer);
			}
		});
		if (this.container) {
			this.enableSticky();
		}
	}
	enableSticky() {
		let rootMargin =
			parseInt(
				getComputedStyle(document.documentElement).getPropertyValue(
					'--header-height',
				),
				10,
			) + 1;
		this.observer = new IntersectionObserver(
			([e]) => {
				e.target.classList.toggle(
					'is-pinned',
					e.intersectionRatio <= 1 && e.intersectionRatio > 0,
				);
			},
			{
				threshold: [1],
				rootMargin: `-${rootMargin}px 0px 0px 0px`,
			},
		);

		this.observer.observe(this.container);
	}

	disconnectedCallback() {
		if (this.observer) {
			this.observer.disconnect();
		}
	}
}
customElements.define('facet-toggle', FacetToggle);

/**
 *  @class
 *  @function FacetFiltersForm
 */
class FacetFiltersForm extends HTMLElement {
	constructor() {
		super();
		this.filterMode = this.dataset.filterMode || 'instant';
		this.form = this.querySelector('form');
		this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

		if (!this.form) return;

		if (this.filterMode === 'instant') {
			this.debouncedOnSubmit = debounce((event) => {
				if (event.target.id === 'compare_toggle') {
					return;
				}
				this.onSubmitHandler(event);
			}, 500);

			this.form.addEventListener(
				'input',
				this.debouncedOnSubmit.bind(this),
			);
			this.bindApplyButtons(false);
		} else {
			this.bindApplyButtons(true);
		}
	}

	bindApplyButtons(submitOnClick) {
		const scope = this.closest('.side-panel-inner') || this;
		scope.querySelectorAll('.facets-apply-button').forEach((btn) => {
			btn.addEventListener('click', (event) => {
				event.preventDefault();
				if (submitOnClick) {
					this.onSubmitHandler({
						preventDefault: () => {},
						target: this.form,
					});
				}
				this.closeFacetDrawer();
			});
		});
	}

	closeFacetDrawer() {
		const drawer = document.getElementById('Facet-Drawer');
		if (
			drawer &&
			typeof OverlayManager !== 'undefined' &&
			OverlayManager.isOpen(drawer)
		) {
			OverlayManager.close(drawer);
		}
	}

	static setListeners() {
		FacetFiltersForm.onHistoryChangeBound = (event) => {
			const searchParams = event.state
				? event.state.searchParams
				: FacetFiltersForm.searchParamsInitial;
			if (searchParams === FacetFiltersForm.searchParamsPrev) return;
			FacetFiltersForm.renderPage(searchParams, null, false);
		};
		window.addEventListener(
			'popstate',
			FacetFiltersForm.onHistoryChangeBound,
		);
	}

	static removeListeners() {
		if (FacetFiltersForm.onHistoryChangeBound) {
			window.removeEventListener(
				'popstate',
				FacetFiltersForm.onHistoryChangeBound,
			);
		}
	}

	static toggleActiveFacets(disable = true) {
		document.querySelectorAll('.js-facet-remove').forEach((element) => {
			element.classList.toggle('disabled', disable);
		});
	}

	static renderPage(searchParams, event, updateURLHash = true) {
		FacetFiltersForm.searchParamsPrev = searchParams;
		const sections = FacetFiltersForm.getSections();
		const container = document.getElementsByClassName('thb-filter-count');
		document
			.getElementById('ProductGridContainer')
			.querySelector('.collection')
			.classList.add('loading');

		for (var item of container) {
			item.classList.add('loading');
		}

		sections.forEach((section) => {
			const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
			const filterDataUrl = (element) => element.url === url;

			if (FacetFiltersForm.filterData.some(filterDataUrl)) {
				FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
			} else {
				FacetFiltersForm.renderSectionFromFetch(url, event);
			}
		});
		if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
	}

	static renderSectionFromFetch(url, event) {
		fetch(url)
			.then((response) => response.text())
			.then((responseText) => {
				const html = responseText;
				FacetFiltersForm.filterData = [
					...FacetFiltersForm.filterData,
					{
						html,
						url,
					},
				];
				FacetFiltersForm.renderFilters(html, event);
				FacetFiltersForm.renderProductGridContainer(html);
				FacetFiltersForm.renderProductCount(html);
			});
	}

	static renderSectionFromCache(filterDataUrl, event) {
		const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
		FacetFiltersForm.renderFilters(html, event);
		FacetFiltersForm.renderProductGridContainer(html);
		FacetFiltersForm.renderProductCount(html);
	}

	static renderProductGridContainer(html) {
		document.getElementById('ProductGridContainer').innerHTML =
			new DOMParser()
				.parseFromString(html, 'text/html')
				.getElementById('ProductGridContainer').innerHTML;
		document.dispatchEvent(new CustomEvent('facets:updated'));
	}

	static renderProductCount(html) {
		const countHtml = new DOMParser()
			.parseFromString(html, 'text/html')
			.getElementById('ProductCount');
		const container = document.getElementsByClassName('thb-filter-count');

		if (countHtml) {
			for (var item of container) {
				item.innerHTML = countHtml.innerHTML;
				item.classList.remove('loading');
			}
		}
	}

	static renderFilters(html, event) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

		[
			{ source: '#FacetFiltersForm', target: '#FacetFiltersForm' },
			{
				source: '#FacetFiltersFormMobile',
				target: '#FacetFiltersFormMobile',
			},
		].forEach(({ source, target }) => {
			const sourceForm = parsedHTML.querySelector(source);
			const targetForm = document.querySelector(target);
			if (!sourceForm || !targetForm) return;

			sourceForm
				.querySelectorAll('collapsible-row')
				.forEach((element) => {
					const targetRow = targetForm.querySelector(
						`collapsible-row[data-index="${element.dataset.index}"]`,
					);
					if (targetRow) {
						targetRow.innerHTML = element.innerHTML;
					}
				});
		});

		FacetFiltersForm.renderActiveFacets(parsedHTML);
		FacetFiltersForm.renderAdditionalElements(parsedHTML);
	}

	static renderActiveFacets(html) {
		const sourceElement = html.querySelector('facet-remove.active-facets');
		const targetElement = document.querySelector(
			'facet-filters-form facet-remove.active-facets',
		);
		const insertAfter = document.querySelector(
			'facet-filters-form .facets--bar-sticky',
		);

		if (sourceElement && targetElement) {
			targetElement.replaceWith(sourceElement.cloneNode(true));
		} else if (sourceElement && insertAfter) {
			insertAfter.insertAdjacentElement(
				'afterend',
				sourceElement.cloneNode(true),
			);
		} else if (targetElement) {
			targetElement.remove();
		}

		FacetFiltersForm.toggleActiveFacets(false);
	}

	static renderAdditionalElements(html) {
		const mobileElementSelectors = ['.mobile-filter-count'];

		mobileElementSelectors.forEach((selector) => {
			if (!html.querySelector(selector)) return;
			document.querySelector(selector).innerHTML =
				html.querySelector(selector).innerHTML;
		});
	}

	static updateURLHash(searchParams) {
		history.pushState(
			{
				searchParams,
			},
			'',
			`${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
		);
	}

	static getSections() {
		return [
			{
				section: document.getElementById('product-grid').dataset.id,
			},
		];
	}

	onSubmitHandler(event) {
		event.preventDefault();
		const formData = new FormData(event.target.closest('form'));
		const searchParams = new URLSearchParams(formData);
		if (
			searchParams.get('filter.v.price.gte') === '0.00' ||
			searchParams.get('filter.v.price.gte') === ''
		) {
			searchParams.delete('filter.v.price.gte');
		}
		if (searchParams.get('filter.v.price.lte') === '') {
			searchParams.delete('filter.v.price.lte');
		}
		if (document.querySelector('.price_checkboxes')) {
			const rangeMax = parseFloat(
				document.querySelector('.price_checkboxes').dataset.rangeMax,
			);
			if (
				searchParams.get('filter.v.price.lte') === rangeMax.toFixed(2)
			) {
				searchParams.delete('filter.v.price.lte');
			}
		} else if (document.querySelector('.price_slider')) {
			if (
				searchParams.get('filter.v.price.lte') ===
				parseFloat(
					document.querySelector('.price_slider').dataset.max,
				).toFixed(2)
			) {
				searchParams.delete('filter.v.price.lte');
			}
		}
		FacetFiltersForm.renderPage(searchParams.toString(), event);
	}

	onActiveFilterClick(event) {
		event.preventDefault();
		FacetFiltersForm.toggleActiveFacets();
		const url =
			event.currentTarget.href.indexOf('?') == -1
				? ''
				: event.currentTarget.href.slice(
						event.currentTarget.href.indexOf('?') + 1,
					);
		FacetFiltersForm.renderPage(url);
	}
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

document.addEventListener('click', (event) => {
	const toggle = event.target.closest('.facet-filter-list__toggle');
	if (!toggle) return;
	event.preventDefault();
	const list = toggle.closest('.facet-filter-list');
	if (!list) return;
	const expanded = list.classList.toggle('facet-filter-list--expanded');
	toggle.setAttribute('aria-expanded', expanded);
});

/**
 *  @class
 *  @function Facetremove
 */
class FacetRemove extends HTMLElement {
	constructor() {
		super();
		this.querySelectorAll('a').forEach((item) => {
			item.addEventListener('click', (event) => {
				event.preventDefault();
				const form =
					this.closest('facet-filters-form') ||
					document.querySelector('facet-filters-form');
				form.onActiveFilterClick(event);
			});
		});
	}
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
	constructor() {
		super();
	}
	connectedCallback() {
		this.compareToggle = this.querySelector('#compare_toggle');
		if (this.compareToggle) {
			this.compareToggle.addEventListener('change', this.onCompare);
			this.compareToggle.checked =
				document.body.classList.contains('compare-true');
		}
	}
	onCompare(e) {
		document.body.classList.toggle('compare-true');
		e.preventDefault();
		return false;
	}
}
customElements.define('facet-toolbar', FacetToolbar);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {
	constructor() {
		super();
	}
	connectedCallback() {
		let slider = this.querySelector('.price_slider'),
			amounts = this.querySelector('.price_slider_amount'),
			args = {
				start: [
					parseFloat(slider.dataset.minValue || 0),
					parseFloat(slider.dataset.maxValue || slider.dataset.max),
				],
				connect: true,
				step: 10,
				direction: document.dir,
				handleAttributes: [
					{ 'aria-label': 'lower' },
					{ 'aria-label': 'upper' },
				],
				range: {
					min: 0,
					max: parseFloat(slider.dataset.max),
				},
			},
			event = new CustomEvent('input'),
			form =
				this.closest('facet-filters-form') ||
				document.querySelector('facet-filters-form');

		if (slider.classList.contains('noUi-target')) {
			slider.noUiSlider.destroy();
		}
		noUiSlider.create(slider, args);
		this.slider = slider;

		slider.noUiSlider.on('update', function (values) {
			amounts.querySelector('.field__input_min').value = values[0];
			amounts.querySelector('.field__input_max').value = values[1];
		});
		slider.noUiSlider.on('change', function (values) {
			if (form.dataset.filterMode !== 'apply') {
				form.querySelector('form').dispatchEvent(event);
			}
		});
	}

	disconnectedCallback() {
		if (this.slider && this.slider.noUiSlider) {
			this.slider.noUiSlider.destroy();
		}
	}
}
customElements.define('price-slider', PriceSlider);

/**
 *  @class
 *  @function PriceCheckboxes
 */
class PriceCheckboxes extends HTMLElement {
	constructor() {
		super();
	}

	connectedCallback() {
		this.hiddenGte = this.querySelector('.price_checkboxes__gte');
		this.hiddenLte = this.querySelector('.price_checkboxes__lte');
		this.checkboxes = this.querySelectorAll('.price_checkboxes__input');
		this.form = this.closest('facet-filters-form')?.querySelector('form');

		this.checkboxes.forEach((checkbox) => {
			checkbox.addEventListener('change', (event) =>
				this.updateHiddenInputs(event),
			);
		});
	}

	updateHiddenInputs(event) {
		const target = event.target;

		if (target.checked) {
			this.checkboxes.forEach((checkbox) => {
				if (checkbox !== target) checkbox.checked = false;
			});
		}

		const checked = [...this.checkboxes].find(
			(checkbox) => checkbox.checked,
		);

		if (!checked) {
			if (this.hiddenGte) this.hiddenGte.value = '';
			if (this.hiddenLte) this.hiddenLte.value = '';
		} else {
			if (this.hiddenGte)
				this.hiddenGte.value = parseFloat(checked.dataset.min).toFixed(
					2,
				);
			if (this.hiddenLte)
				this.hiddenLte.value = parseFloat(checked.dataset.max).toFixed(
					2,
				);
		}

		if (this.form) {
			const facetForm = this.closest('facet-filters-form');
			if (!facetForm || facetForm.dataset.filterMode !== 'apply') {
				this.form.dispatchEvent(new Event('input'));
			}
		}
	}
}
customElements.define('price-checkboxes', PriceCheckboxes);
